import { useState } from "react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { useCan } from "@/hooks/use-operator";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { QuestionDialog } from "@/features/food-sensory/dialogs/sensory-question-dialog";
import type {
  BrewSensoryCategory,
  BrewSensoryFormId,
  BrewSensoryQuestionId,
} from "@brewbase/db/types";

type Form = RouterOutputs["brewSensory"]["forms"]["list"][number];
type Question = RouterOutputs["brewSensory"]["questions"]["list"][number];
type QuestionType = Question["question_type"];

const QUESTION_TYPE_LABELS: Record<string, string> = {
  scale: "Scale",
  short_text: "Short Text",
  paragraph: "Paragraph",
  single_choice: "Single Choice",
  multi_choice: "Multi Choice",
  yes_no: "Yes / No",
};

const CATEGORY_BADGE: Record<string, string> = {
  aroma: "bg-violet-100 text-violet-700",
  taste: "bg-amber-100 text-amber-700",
};

function CategoryBadge({ category }: { category: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${CATEGORY_BADGE[category] ?? "bg-muted text-muted-foreground"}`}
    >
      {category}
    </span>
  );
}

export function BrewSensoryFormsSection() {
  const canEdit = useCan("sensory-template:edit");
  const [selectedId, setSelectedId] = useState<BrewSensoryFormId | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: forms = [], isLoading } =
    trpc.brewSensory.forms.list.useQuery();

  // Default the selection to the first form once the list loads.
  const selected =
    forms.find((f) => f.id === selectedId) ?? forms[0] ?? null;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">
          A form is a reusable set of questions with a category (aroma or
          taste). Sessions and templates each pick one aroma form and,
          optionally, one taste form. Sessions snapshot a form's questions at
          activation, so edits here never affect sessions already started.
        </p>
      </div>

      <div className="flex gap-6">
        {/* Master: form list */}
        <div className="w-80 shrink-0 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Forms
            </h3>
            {canEdit && !creating && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => setCreating(true)}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                New
              </Button>
            )}
          </div>

          {creating && (
            <NewFormEditor
              onDone={(created) => {
                setCreating(false);
                if (created) setSelectedId(created.id);
              }}
            />
          )}

          {isLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="space-y-1">
              {forms.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setSelectedId(f.id)}
                  className={`flex w-full items-center justify-between gap-2 border px-3 py-2 text-left text-sm ${
                    selected?.id === f.id
                      ? "border-foreground/40 bg-muted"
                      : "border-transparent hover:bg-muted/60"
                  }`}
                >
                  <span className="min-w-0 truncate">{f.name}</span>
                  <CategoryBadge category={f.category} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail: selected form's questions */}
        <div className="flex-1 min-w-0 max-w-3xl">
          {selected ? (
            <FormDetail
              key={selected.id}
              form={selected}
              canEdit={canEdit}
              onArchived={() => setSelectedId(null)}
            />
          ) : (
            <p className="py-4 text-sm text-muted-foreground">
              {forms.length === 0
                ? "No forms yet. Create one to start adding questions."
                : "Select a form to edit its questions."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function NewFormEditor({
  onDone,
}: {
  onDone: (created: Form | null) => void;
}) {
  const utils = trpc.useUtils();
  const [name, setName] = useState("");
  const [category, setCategory] = useState<BrewSensoryCategory>(
    "aroma" as BrewSensoryCategory,
  );

  const createMutation = trpc.brewSensory.forms.create.useMutation({
    onSuccess: (created) => {
      utils.brewSensory.forms.list.invalidate();
      onDone(created);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Form name is required.");
      return;
    }
    createMutation.mutate({ name: trimmed, category });
  };

  return (
    <div className="space-y-2 border bg-card px-3 py-3">
      <div className="space-y-1.5">
        <Label htmlFor="new-form-name" className="text-xs">
          Name
        </Label>
        <Input
          id="new-form-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Standard Aroma Form"
          className="h-8 text-sm"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Category</Label>
        <Select
          value={category}
          onValueChange={(v) => setCategory(v as BrewSensoryCategory)}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="aroma">Aroma</SelectItem>
            <SelectItem value="taste">Taste</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => onDone(null)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          disabled={createMutation.isPending}
          onClick={handleSave}
        >
          {createMutation.isPending ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <Check className="h-3 w-3 mr-1" />
          )}
          Create
        </Button>
      </div>
    </div>
  );
}

function FormDetail({
  form,
  canEdit,
  onArchived,
}: {
  form: Form;
  canEdit: boolean;
  onArchived: () => void;
}) {
  const utils = trpc.useUtils();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(form.name);

  const renameMutation = trpc.brewSensory.forms.update.useMutation({
    onSuccess: () => {
      utils.brewSensory.forms.list.invalidate();
      setRenaming(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const archiveMutation = trpc.brewSensory.forms.archive.useMutation({
    onSuccess: () => {
      utils.brewSensory.forms.list.invalidate();
      onArchived();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {renaming ? (
          <>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-8 max-w-xs text-sm"
            />
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={renameMutation.isPending}
              onClick={() => {
                const trimmed = name.trim();
                if (!trimmed) {
                  toast.error("Form name is required.");
                  return;
                }
                renameMutation.mutate({ id: form.id, name: trimmed });
              }}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setName(form.name);
                setRenaming(false);
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <h3 className="text-sm font-medium">{form.name}</h3>
            <CategoryBadge category={form.category} />
            {canEdit && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => setRenaming(true)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <DeleteConfirmDialog
                  title="Archive form?"
                  description={`Archive the "${form.name}" form? It will no longer be selectable for new sessions or templates. Sessions already created keep their snapshot copy.`}
                  isDeleting={archiveMutation.isPending}
                  onConfirm={() => archiveMutation.mutate(form.id)}
                />
              </div>
            )}
          </>
        )}
      </div>

      <FormQuestions formId={form.id} canEdit={canEdit} />
    </div>
  );
}

function FormQuestions({
  formId,
  canEdit,
}: {
  formId: BrewSensoryFormId;
  canEdit: boolean;
}) {
  const utils = trpc.useUtils();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState<Question | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Question | null>(null);

  const { data: questions = [], isLoading } =
    trpc.brewSensory.questions.list.useQuery({ form_id: formId });

  const invalidate = () =>
    utils.brewSensory.questions.list.invalidate({ form_id: formId });

  const createMutation = trpc.brewSensory.questions.create.useMutation({
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.brewSensory.questions.update.useMutation({
    onSuccess: () => {
      invalidate();
      setEditQuestion(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.brewSensory.questions.delete.useMutation({
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const reorderMutation = trpc.brewSensory.questions.reorder.useMutation({
    onSuccess: invalidate,
    onError: (err) => toast.error(err.message),
  });

  const handleMove = (index: number, direction: "up" | "down") => {
    const next = [...questions];
    const swap = direction === "up" ? index - 1 : index + 1;
    [next[index], next[swap]] = [next[swap], next[index]];
    reorderMutation.mutate(next.map((q, i) => ({ id: q.id, display_order: i })));
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading questions...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {questions.length === 0
            ? "No questions configured yet."
            : `${questions.length} question${questions.length !== 1 ? "s" : ""} configured.`}
        </p>
        {canEdit && (
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add Question
          </Button>
        )}
      </div>

      {questions.length > 0 && (
        <div className="space-y-2">
          {questions.map((q, i) => (
            <div
              key={q.id}
              className="flex items-start gap-3 border bg-card px-4 py-3"
            >
              {canEdit && (
                <div className="flex flex-col gap-0.5 shrink-0 mt-0.5">
                  <button
                    type="button"
                    onClick={() => handleMove(i, "up")}
                    disabled={i === 0 || reorderMutation.isPending}
                    className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMove(i, "down")}
                    disabled={
                      i === questions.length - 1 || reorderMutation.isPending
                    }
                    className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              <span className="text-xs font-mono text-muted-foreground shrink-0 mt-1 w-4 text-right">
                {i + 1}.
              </span>

              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-sm leading-snug">{q.text}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                    {QUESTION_TYPE_LABELS[q.question_type] ?? q.question_type}
                  </span>
                  {q.question_type === "scale" && (
                    <span className="text-[10px] text-muted-foreground">
                      {q.min_value}–{q.max_value}
                      {q.min_label || q.max_label
                        ? ` (${[q.min_label, q.max_label].filter(Boolean).join(" → ")})`
                        : ""}
                    </span>
                  )}
                  {(q.question_type === "single_choice" ||
                    q.question_type === "multi_choice") &&
                    !!q.options &&
                    (q.options as string[]).length > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        {(q.options as string[]).length} option
                        {(q.options as string[]).length !== 1 ? "s" : ""}
                      </span>
                    )}
                  {q.required && (
                    <span className="text-[10px] text-destructive font-medium">
                      Required
                    </span>
                  )}
                </div>
              </div>

              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => setEditQuestion(q)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => setDeleteTarget(q)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <QuestionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        isPending={createMutation.isPending}
        testType={"hedonic"}
        onSave={(data) =>
          createMutation.mutate({
            form_id: formId,
            display_order: questions.length,
            text: data.text,
            question_type: data.question_type as QuestionType,
            options: data.options,
            min_value: data.min_value,
            max_value: data.max_value,
            min_label: data.min_label,
            max_label: data.max_label,
            required: data.required,
          })
        }
      />

      {editQuestion && (
        <QuestionDialog
          open={!!editQuestion}
          onOpenChange={(v) => {
            if (!v) setEditQuestion(null);
          }}
          initial={editQuestion as any}
          isPending={updateMutation.isPending}
          testType={"hedonic"}
          onSave={(data) =>
            updateMutation.mutate({
              id: editQuestion.id,
              text: data.text,
              question_type: data.question_type as QuestionType,
              options: data.options,
              min_value: data.min_value,
              max_value: data.max_value,
              min_label: data.min_label,
              max_label: data.max_label,
              required: data.required,
            })
          }
        />
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => {
          if (!v) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete question?</AlertDialogTitle>
            <AlertDialogDescription>
              This soft-deletes the question. Existing closed sessions keep their
              snapshot copy of it, so historical results are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                deleteTarget &&
                deleteMutation.mutate(
                  deleteTarget.id as unknown as BrewSensoryQuestionId,
                )
              }
            >
              {deleteMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
